import { DatabaseService } from 'src/app/services/database.service';
import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular/lazy';
import { APIService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { Router } from '@angular/router';
import { ParkedCartService } from 'src/app/services/parked-cart.service';

@Injectable()
export class PendingOrderService {

    constructor(
        public alertController: AlertController,
        private router: Router,
        private authService: AuthService,
        private apiService: APIService,
        private databaseService: DatabaseService,
        private parkedCartService: ParkedCartService
    ) { }

    addPendingOrder(order_data,idempotency_key) {
        return new Promise((resolve, reject) => {
            let pending_order = {};
            pending_order["order_data"] = order_data;
            pending_order["idempotency_key"] = idempotency_key;
            this.databaseService.pending_order.add(pending_order)
                .then((id) => {
                    resolve(id);
                })
                .catch(() => {
                    reject();
                });
        });
    }

    deletePendingOrder(pending_order) {
        return new Promise<void>((resolve, reject) => {
            this.databaseService.pending_order
                .delete(pending_order.id)
                .then(() => {
                    resolve();
                })
                .catch(() => {
                    reject();
                });
        });
    }

    getPendingOrders() {
        return new Promise((resolve, reject) => {
            this.databaseService.pending_order.toArray().then((pending_orders) => {
                resolve(pending_orders);
            })
            .catch(() => {
                reject();
            });
        });
    }

    // NOTE: resolves once the order is added or has failed, so the next one is still sent; only rejects when logged out
    processPendingOrder(pending_order) {
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.addOrder(pending_order.order_data, pending_order.idempotency_key).then((order) => {
                    // TODO: update payment intents for stripe terminal so has correct description, metadata, shipping
                    this.deletePendingOrder(pending_order).then(() => {
                        if(pending_order.email_invoice_to_email_address) {
                            this.apiService.sendOrderInvoiceEmail(order['id'], pending_order.email_invoice_to_email_address).catch(() => {});
                        }
                        if(pending_order.email_confirmation_to_email_address) {
                            this.apiService.sendOrderConfirmationEmail(order['id'], pending_order.email_confirmation_to_email_address).catch(() => {});
                        }
                        if(pending_order.order_data['parked_cart_id']) {
                            this.parkedCartService.deleteParkedCart(pending_order.order_data['parked_cart_id']).catch(() => {});
                        }
                        resolve();
                    })
                    .catch(() => {
                        resolve();
                    });
                })
                .catch((error) => {
                    if(error.status) {
                        // don't throw error if there's a conflict (i.e. idempotency key being replayed)
                        if(error.status != 409) {
                            if(error.status === 400) {
                                this.presentAlert("Unknown error", "Unfortunately something went wrong when trying to add a pending order.");
                            } else if(error.status === 403) {
                                this.presentAlert("Insufficient permissions", "You don't have permission to add an order.");
                            } else if(error.status === 404 || error.status === 500 || error.status === 501 || error.status === 502 || error.status === 503 || error.status === 504 || error.status === 0) {
                                this.presentAlert("Unavailable", "Unfortunately we're unable to add a pending order at time.");
                            }
                            resolve();
                        }
                        // delete order if there's a conflict
                        else {
                            this.deletePendingOrder(pending_order).then(() => {
                                resolve();
                            })
                            .catch(() => {
                                resolve();
                            });
                        }
                    }
                    else {
                        resolve();
                    }
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
                reject();
            });
        });
    }

    processPendingOrders() {
        return new Promise<void>((resolve, reject) => {
            this.databaseService.pending_order.toArray().then(async (pending_orders) => {
                try {
                    for (let i = 0; i < pending_orders.length; i++) {
                        // NOTE: sends each order 5 seconds apart to avoid overloading the Xero API
                        if(i > 0) {
                            await new Promise((resolve_wait) => setTimeout(resolve_wait, 5000));
                        }
                        await this.processPendingOrder(pending_orders[i]);
                    }
                    resolve();
                }
                catch {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    updatePendingOrder(id, data) {
        return new Promise<void>((resolve, reject) => {
            this.databaseService.pending_order.update(id, data).then(() => {
                resolve();
            })
            .catch(() => {
                reject();
            });
        });
    }
    
    async presentAlert(header, message) {
        const alert = await this.alertController.create({
            header: header,
            message: message,
            buttons: ['OK'],
        });
        await alert.present();
    }

}
